import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { matchDimensions, productsToShoppingItems } from 'products';
import { RootState } from 'ducks';
import {
  CoreApi,
  Product,
  ProductEntity,
  ShoppingItemWithPrice,
  NonMarketProduct
} from '@inter-ikea-storageone/range';
import * as R from 'ramda';
import {
  getProductEntities,
  getSelectedEntities,
  getSelectedEntity,
  getShoppingBagEntities
} from './core';
import { Dispatch } from 'react';
import { PlannerConfig, ProductGroup } from 'rangeConfig/@types';
import { ProductBatch } from 'utils/batch';

export interface SelectedProduct extends Product {
  entityId: number;
}

export type Filter = {
  productIdList: string[];
  filterState: any;
};

export type Sorting = {
  id: string;
};

export type ProductFilter = {
  [filterId: string]: Filter;
};

export type ProductSorting = {
  [sortingId: string]: Sorting;
};

export type ProductFilters = {
  [productGroupId: string]: ProductFilter;
};

export type BatchSortFn = (batchA: ProductBatch, batchB: ProductBatch) => number;

export type ProductGroupState = {
  all: string[];
  filtered: string[];
  toggled: string[];
};

export interface ProductState {
  inMarket: Product[]; // Valid products in market
  replacementProducts: Product[]; // Valid products but replaces products that is also still valid so shouldn't be displayed in planner
  unavailable: Product[]; // Invalid products where we get product info (has been sold in market previously)
  notInMarket: NonMarketProduct[]; // Invalid products where we don't get any product info (never been sold in market)
  coreSpecific: NonMarketProduct[]; // Products that are not acctual IKEA products
  selectedGroupId: string;
  filters: ProductFilters;
  toggles: ProductFilters;
  sorting: Record<string, { id: string; compare: BatchSortFn | undefined }>;
  productGroups: Record<string, ProductGroupState>;
  articlesInMarket: string[];
  shoppingItemsFromLegacyCode: ShoppingItemWithPrice[];
  showDrawerAccessoriesInfoMessage: boolean;
}

type ProductSucces = {
  inMarket: Product[];
  replacementProducts: Product[];
  unavailableProducts: Product[];
  productsNotInMarket: NonMarketProduct[];
  coreSpecificProducts: NonMarketProduct[];
};

export type FilterAction = {
  productIdList: string[];
  productGroupId: string;
  id: string;
  filterState: Filter | object;
};

export type SortingAction = {
  compare: BatchSortFn | undefined;
  identifier: string;
  id: string;
};

export type SceneFilterAction = {
  productIdList: string[];
  productGroupId: string;
};

export type ConfigureProductAction = {
  [groupId: string]: ProductGroupState;
};

export const initialState: ProductState = {
  inMarket: [],
  replacementProducts: [],
  unavailable: [],
  notInMarket: [],
  coreSpecific: [],
  selectedGroupId: '',
  filters: {},
  toggles: {},
  sorting: {},
  productGroups: {},
  articlesInMarket: [],
  shoppingItemsFromLegacyCode: [],
  showDrawerAccessoriesInfoMessage: true
};

export const attachProductSubscribers = (dispatch: Dispatch<any>, coreApi: CoreApi) => {
  coreApi.subscribe('loadLegacyShoppingItems', legacyShoppingItems =>
    dispatch(setLegacyShoppingItems(legacyShoppingItems))
  );
};

export const attachDefaultsFromPlannerConfig = (
  dispatch: Dispatch<any>,
  plannerConfig: PlannerConfig
) => {
  plannerConfig.productGroups.forEach(productGroup => {
    productGroup.mainMenu.subMenu?.options.forEach(subMenu => {
      if (subMenu.sorting?.length) {
        const { id, compare } = subMenu.sorting[0];

        dispatch(
          sortingApplied({
            id,
            compare,
            identifier: subMenu.id
          })
        );
      }
    });

    if (productGroup.mainMenu.sorting?.length) {
      const { id, compare } = productGroup.mainMenu.sorting[0];

      dispatch(
        sortingApplied({
          id,
          compare,
          identifier: productGroup.id
        })
      );
    }
  });
};

const productSlice = createSlice({
  initialState,
  name: 'ProductSlice',
  reducers: {
    getProductsSuccess: (state, action: PayloadAction<ProductSucces>) => {
      const {
        inMarket,
        replacementProducts,
        unavailableProducts,
        productsNotInMarket,
        coreSpecificProducts
      } = action.payload;
      state.inMarket = inMarket;
      state.replacementProducts = replacementProducts;
      state.unavailable = unavailableProducts;
      state.notInMarket = productsNotInMarket;
      state.coreSpecific = coreSpecificProducts;
    },
    configureProductGroups: (state, action: PayloadAction<ConfigureProductAction>) => {
      state.productGroups = action.payload;
    },
    selectGroup: (state, action: PayloadAction<string>) => {
      state.selectedGroupId = action.payload;
    },
    filtersReset: (state, action: PayloadAction<string>) => {
      const selectedGroupId = action.payload;
      state.filters[selectedGroupId] = {};
      state.productGroups[selectedGroupId].filtered = state.productGroups[selectedGroupId].all;
    },
    resetAllFilters: state => {
      state.filters = {};
      state.toggles = {};
      state.productGroups = Object.assign(
        {},
        ...Object.entries(state.productGroups).map(
          ([productGroup, groupState]: [string, ProductGroupState]) => {
            return {
              [productGroup]: {
                ...groupState,
                filtered: groupState.all
              }
            };
          }
        )
      );
    },
    sortingApplied: (state, action: PayloadAction<SortingAction>) => {
      const { compare, identifier, id } = action.payload;

      if (!compare) {
        state.sorting[identifier] = { id, compare };
        return;
      }

      // Safely compare
      const compareFn: BatchSortFn = (...args) => {
        try {
          return compare(...args);
        } catch {
          return 0;
        }
      };

      state.sorting[identifier] = {
        id,
        compare: compareFn
      };
    },
    filterApplied: (state, action: PayloadAction<FilterAction>) => {
      const { productIdList, productGroupId, id, filterState } = action.payload;
      if (!state.filters[productGroupId]) {
        state.filters[productGroupId] = {};
      }
      state.filters[productGroupId][id] = {
        productIdList,
        filterState
      };

      if (state.productGroups[productGroupId] && state.filters[productGroupId]) {
        state.productGroups[productGroupId].filtered = Object.values(
          state.filters[productGroupId]
        ).reduce((acc, filter) => {
          return R.intersection(acc, filter.productIdList);
        }, state.productGroups[productGroupId].all);
      }
    },
    toggleApplied: (state, action: PayloadAction<FilterAction>) => {
      const { productIdList, id, filterState } = action.payload;
      const productGroupId = state.selectedGroupId;

      if (!state.toggles[productGroupId]) {
        state.toggles[productGroupId] = {};
      }
      state.toggles[productGroupId][id] = {
        productIdList,
        filterState
      };

      const group = state.productGroups[productGroupId];

      if (group) {
        group.toggled = Object.values(state.toggles[productGroupId]).reduce((acc, toggle) => {
          return R.intersection(acc, toggle.productIdList);
        }, group.all);
      }
    },
    setArticlesInMarket: (state, action: PayloadAction<string[]>) => {
      state.articlesInMarket = action.payload;
    },
    setLegacyShoppingItems: (state, action: PayloadAction<ShoppingItemWithPrice[]>) => {
      state.shoppingItemsFromLegacyCode = action.payload;
    },
    setShowDrawerAccessoriesInfoMessage: (state, action) => {
      state.showDrawerAccessoriesInfoMessage = action.payload;
    }
  }
});

export const filterCheck = (state: RootState) => (productGroupId: string) =>
  Object.values(state.products.filters?.[productGroupId] || {});

export const hasActiveFilters = (state: RootState) => (selectedGroup: ProductGroup | undefined) => {
  const filtersApplied = filterCheck(state);

  const filterObject = filtersApplied(selectedGroup?.id || '')
    .map(g => g.filterState)
    .filter(g => !Object.keys(g).includes('design'));

  return !!filterObject.map(f => Object.values(f)[0]).some(value => value === true);
};

export const getToggles = (state: RootState) => state.products.toggles;

export const getFilters = (state: RootState) => state.products.filters;

export const getMarketProducts = (state: RootState) => state.products.inMarket;

export const getUnavailableProducts = (state: RootState) => state.products.unavailable;

export const getProductsNotInMarket = (state: RootState) => state.products.notInMarket;

export const getCoreSpecificProducts = (state: RootState) => state.products.coreSpecific;

export const getReplacementProducts = (state: RootState) => state.products.replacementProducts;

export const getArticlesInMarket = (state: RootState) => state.products.articlesInMarket;

export const getProductById = (state: RootState, productId: string) => {
  const products = getMarketProducts(state);

  return products.find(product => product.coreProductId === productId);
};

export const getProductsByIdsOld = (state: RootState, productIds: string[]): Product[] => {
  if (!productIds) return [];
  const products = getMarketProducts(state);

  return products.filter(product => productIds.includes(product.coreProductId));
};

export const getProductsByIds = createSelector(
  [getMarketProducts, (products, productIds) => productIds],
  (products, productIds) => {
    if (!productIds) return [];
    return products.filter((product: any) => productIds.includes(product.coreProductId));
  }
);

export const getToggleState = createSelector([getToggles], toggles => {
  return (productGroupId: string, filterId: string) =>
    toggles?.[productGroupId]?.[filterId]?.filterState || {};
});

export const getFilterState = createSelector([getFilters], filters => {
  return (productGroupId: string, filterId: string) =>
    filters?.[productGroupId]?.[filterId]?.filterState || {};
});

export const getAllValidProducts = createSelector(
  [getMarketProducts, getReplacementProducts],
  (marketProducts, replacementProducts) => {
    return [...marketProducts, ...replacementProducts];
  }
);

export const getSelectedGroupId = (state: RootState): string => state.products.selectedGroupId;

export const getLegacyShoppingItems = (state: RootState): ShoppingItemWithPrice[] =>
  state.products.shoppingItemsFromLegacyCode;

export const showDrawerAccessoriesInfoMessage = (state: RootState) =>
  state.products.showDrawerAccessoriesInfoMessage;

export const getProductInSceneCount = (
  state: RootState,
  productId: string,
  cluster?: Product[]
): number => {
  const entitiesInScene = state.core.productEntities;

  //Just return if we don't have any entities
  if (!entitiesInScene) {
    return 0;
  }

  if (!cluster) {
    const productInSceneCount = entitiesInScene
      .map(entity => {
        return entity.productId === productId;
      })
      .filter(Boolean).length;

    return productInSceneCount;
  } else {
    const productsInSceneCount: number = entitiesInScene
      .map(entity => {
        return cluster
          .map((clusterItem: any) => {
            return clusterItem.coreProductId === entity.productId;
          })
          .filter(Boolean);
      })
      .flat().length;

    return productsInSceneCount;
  }
};

export const getSelectedProducts = createSelector(
  [getAllValidProducts, getSelectedEntities],
  (products, selectedEntities): SelectedProduct[] => {
    return selectedEntities.map(entity => {
      const product = products.find(p => p.coreProductId === entity.productId) as Product;
      return {
        ...product,
        entityId: entity.entityId
      };
    });
  }
);

export const getSelectedProduct = createSelector(
  [getAllValidProducts, getSelectedEntity],
  (products, selectedEntity): SelectedProduct | null => {
    if (!selectedEntity) {
      return null;
    }

    const product = products.find(product => product.coreProductId === selectedEntity.productId);

    if (!product) {
      return null;
    }

    return {
      ...product,
      entityId: selectedEntity.entityId
    };
  }
);

export const getProductsInScene = createSelector(
  [getAllValidProducts, getProductEntities],
  (products, entities) => {
    const res: Product[] = [];
    entities.forEach((entity: ProductEntity) => {
      const product = products.find(product => product.coreProductId === entity.productId);
      if (product) {
        res.push({ ...product, entityId: entity.entityId });
      }
    });
    return res;
  }
);

export const getShoppingItemsInScene = createSelector(
  [getAllValidProducts, getShoppingBagEntities],
  (products, entities) => {
    const res: Product[] = [];
    entities.forEach((entity: ProductEntity) => {
      const product = products.find(product => product.coreProductId === entity.productId);
      if (product) {
        res.push({ ...product, entityId: entity.entityId });
      }
    });
    return res;
  }
);

export const getFiltersForProductGroup = createSelector(
  [getFilters, getSelectedGroupId],
  (filters, selectedGroupOId) => {
    return filters[selectedGroupOId] || {};
  }
);

export type ProductMap = {
  [coreProductId: string]: Product;
};

export const getProductGroups = (state: RootState) => state.products.productGroups;

export const getSelectedProductGroup = (state: RootState) =>
  state.products.productGroups[state.products.selectedGroupId];

export const getSelectedProductFilters = (state: RootState) =>
  state.products.filters[state.products.selectedGroupId];

export const getConfigSorting = (identifier: string) => (state: RootState) =>
  state.products.sorting[identifier] as RootState['products']['sorting'][string] | undefined;

export const getProductsForGroup = createSelector(
  [getMarketProducts, getSelectedProductGroup],
  (products, productGroup) => {
    if (!productGroup) return [];

    return products.filter(product => productGroup.all.includes(product.coreProductId));
  }
);

export const getFramesProductGroup = (state: RootState) => state.products.productGroups['frames'];

export const getAllFrameProducts = createSelector(
  [getMarketProducts, getFramesProductGroup],
  (products, framesGroup) => {
    if (!framesGroup) return [];

    return products.filter(product => framesGroup.all.includes(product.coreProductId));
  }
);

export const createProductMap = (products: Product[]) =>
  products.reduce((acc, curr) => {
    return {
      ...acc,
      [curr.coreProductId]: curr
    };
  }, {} as ProductMap);

export const hasFilteredProducts = createSelector(
  [getSelectedProductFilters],
  (filtersForGroup: ProductFilter): boolean => {
    if (!filtersForGroup) {
      return false;
    }

    const activeFilterCount = Object.keys(filtersForGroup)
      .map((filterGroupKey: string) => {
        let allFilters = [];

        if (filterGroupKey) {
          allFilters = Object.keys(filtersForGroup[filterGroupKey].filterState).map(
            (filterStateKey: string) => {
              return filtersForGroup[filterGroupKey].filterState[filterStateKey];
            }
          );
        }

        return allFilters;
      })
      .flat()
      .filter(Boolean).length;

    if (activeFilterCount < 1) {
      return false;
    }

    return true;
  }
);

export const getVisibleProductsForGroup = createSelector(
  [getMarketProducts, getSelectedProductGroup],
  (products, productGroup) => {
    if (!productGroup) {
      return [];
    }

    const filters = [productGroup.toggled, productGroup.filtered];
    const visible = filters.reduce((acc, array) => {
      return R.intersection(acc, array);
    });

    return products.filter(product => visible.includes(product.coreProductId));
  }
);

export const hasProducts = createSelector(
  getMarketProducts,
  (products): boolean => products.length > 0
);

export const getReplaceableProducts = (state: RootState, product?: Product | null): Product[] => {
  if (!product) return [];
  const { type, communicatedDimensions } = product;
  if (!type || !communicatedDimensions) {
    return [];
  }
  return R.sort(
    (a, b) => (a.appearances[0].color < b.appearances[0].color ? 1 : -1),
    state.products.inMarket.filter(
      product =>
        product.type === type &&
        product.communicatedDimensions &&
        matchDimensions(product.communicatedDimensions, communicatedDimensions, [
          'depth',
          'width',
          'height'
        ])
    )
  );
};

export const getShoppingProducts = () =>
  createSelector([getShoppingItemsInScene], products => {
    const shoppingItems = productsToShoppingItems(products);
    return shoppingItems;
  });

export const compareAppearances = (p1: Product, p2: Product) => {
  return (
    p1.appearances.every(({ id }) => p2.appearances.find(a => a.id === id)) &&
    p2.appearances.every(({ id }) => p1.appearances.find(a => a.id === id))
  );
};

export const getProductGroupInformation = createSelector(
  getVisibleProductsForGroup,
  getSelectedGroupId,
  getProductsForGroup,
  (visibleProducts, selectedGroupId, productsInGroup) => {
    return {
      visibleProducts,
      selectedGroupId,
      productsInGroup
    };
  }
);

export const {
  configureProductGroups,
  filterApplied,
  filtersReset,
  sortingApplied,
  getProductsSuccess,
  resetAllFilters,
  selectGroup,
  setArticlesInMarket,
  setLegacyShoppingItems,
  setShowDrawerAccessoriesInfoMessage,
  toggleApplied
} = productSlice.actions;

export default productSlice.reducer;
